import { observer } from 'mobx-react-lite';
import { useStoreContext } from 'store/app';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import './collectionCard.styl';
import { useTranslationsContext } from 'store/translations';
import Ticket from '../../assets/img/ticket.png';
import { ISnake, LotteryRewardTypes } from 'src/types';
import { PresentModal } from 'components/modal/presentModal';
import { Button } from 'components/core';
import { useNavigate } from 'react-router';
import { routes } from 'utils/router';

interface Props {
    collectionId: number;
    activeSnake?: number;
    onPrizeModalClick?: () => void;
    setSelectedPresentId?: (id: number) => void;
    isPreview?: boolean;
}

const deformationDuration = 300;

export const CollectionCard = observer(
    ({ collectionId, activeSnake, onPrizeModalClick, setSelectedPresentId, isPreview }: Props) => {
        const {
            catalogConfig,
            userState,
            questsConfig,
            isCollectionFinished,
            isMainPartFinished,
            logOperation,
            lotteryConfig,
            setRevolverPrize
        } = useStoreContext();
        const navigate = useNavigate();
        const { translate } = useTranslationsContext();
        const [selectedSnake, setSelectedSnake] = useState<ISnake | null>(null);
        const [scaleActiveSnakes, setScaleActiveSnakes] = useState<boolean>(false);
        const elemRef = useRef<HTMLDivElement | null>(null);

        const collectionConfig = useMemo(() => {
            return catalogConfig?.collections.find((col) => col.id === collectionId)!;
        }, [collectionId, catalogConfig]);

        const collectionState = useMemo(() => {
            return userState?.collections?.find((col) => col.id === collectionId);
        }, [collectionId, userState]);

        const snakes = useMemo(() => {
            return collectionConfig.snakes.map((snakeId) => {
                return catalogConfig?.snakes.find((snakeconf) => snakeconf.id === snakeId)!;
            });
        }, [collectionConfig, collectionState]);

        const snakesAmount = useMemo(() => {
            return snakes.reduce((acc, snake) => {
                let snakeCount = collectionState?.snakes.includes(snake.id) ? 1 : 0;
                if (snakeCount) {
                    snakeCount += userState?.free_snakes?.filter((x) => x === snake.id).length || 0;
                }
                acc[snake.id] = snakeCount;
                return acc;
            }, {} as Record<number, number>);
        }, [snakes, collectionState, userState]);

        const canExchange =
            !isCollectionFinished && !isMainPartFinished && Object.values(snakesAmount).some((x) => x > 1);

        const {
            custom_params: { bg, img, shadow, title },
            lotteryId
        } = collectionConfig;

        const isVip = !!img;

        const collectionName = title || collectionConfig.name;

        const handleSnakesBlockClick = (snake: ISnake) => {
            if (isPreview) {
                return;
            }
            logOperation('collections', 'snake_view', snake.id);
            setSelectedPresentId?.(snake.id);
            setSelectedSnake(snake);
        };

        const isCompleted = (collectionState?.snakes.length || 0) >= 3;
        const isUnrolled = isCompleted && lotteryConfig && lotteryConfig[lotteryId].availableTicket;

        const rewardByPromo = useMemo(
            () => questsConfig?.rewards?.find((reward) => reward?.promo_code && reward.collection_id === collectionId),
            [userState, collectionId]
        );

        useLayoutEffect(() => {
            if (activeSnake != null && collectionConfig.snakes.includes(activeSnake) && elemRef.current) {
                setTimeout(() => {
                    elemRef.current!.scrollIntoView({ behavior: 'smooth' });
                    setTimeout(() => {
                        setScaleActiveSnakes(true);
                        setTimeout(() => {
                            setScaleActiveSnakes(false);
                            setTimeout(() => {
                                setScaleActiveSnakes(true);
                                setTimeout(() => {
                                    setScaleActiveSnakes(false);
                                }, deformationDuration);
                            }, deformationDuration);
                        }, deformationDuration);
                    }, 1000);
                }, 200);
            }
        }, [activeSnake, elemRef, elemRef.current]);

        return (
            <div
                id={collectionId + ''}
                className={cn('collection-card', { vip: isVip, preview: isPreview })}
                style={{ background: bg, boxShadow: shadow }}
                ref={(ref) => {
                    elemRef.current = ref;
                }}
            >
                {isVip ? <img className="collection-card-vip-image" src={img} /> : null}
                <div className="collection-card-title">{collectionName}</div>
                <div className="collection-card-desc">
                    {isCompleted
                        ? translate('snake.collection.completed')
                        : translate('collected.of', {
                              count: collectionState?.snakes.length || 0,
                              of: collectionConfig.snakes.length
                          })}
                </div>
                <div className="collection-card-reward-row">
                    <span className="collection-card-reward-text">{translate('reward.name')}</span>
                    <span className="collection-card-reward-bubble">
                        <img className="collection-card-reward-ticket" src={Ticket}></img>
                        {translate('plus.ticket')}
                    </span>
                    <span className="collection-card-reward-bubble">{translate('plus.gift')}</span>
                </div>
                <div className="collection-card-snakes-row">
                    {snakes.map((snake) => {
                        const amount = snakesAmount[snake.id];
                        const thisExchangeable = amount > 1;

                        return (
                            <div
                                className={cn('collection-card-snakes-block', {
                                    active: activeSnake === snake.id,
                                    scaled: activeSnake === snake.id && scaleActiveSnakes
                                })}
                                key={snake.id}
                                onClick={() => handleSnakesBlockClick(snake)}
                            >
                                <img
                                    className={cn('collection-card-snakes-block-image', { blured: amount === 0 })}
                                    src={snake.custom_params.img}
                                />
                                <div className="collection-card-snakes-block-amount">
                                    {translate('snake.amount', { count: amount })}
                                </div>
                                {canExchange ? (
                                    <div
                                        className={cn('collection-card-snakes-block-exchange', {
                                            invisible: !thisExchangeable
                                        })}
                                    >
                                        {translate('snake.exchange', {}, 'Обменять')}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
                {isUnrolled && !isPreview ? (
                    <Button
                        className="collection-card-button"
                        onClick={() => navigate(routes.wheel, { state: { collectionId } })}
                    >
                        Крутить колесо удачи
                    </Button>
                ) : null}
                {rewardByPromo?.promo_code ? (
                    <Button
                        className="collection-card-button"
                        disabled={isPreview}
                        onClick={() => {
                            if (isPreview) return;
                            setRevolverPrize(rewardByPromo);
                            navigate(routes.main, {
                                state: {
                                    openPresentModal: true
                                }
                            });
                        }}
                    >
                        {(rewardByPromo?.lottery_reward_type === LotteryRewardTypes.CODE
                            ? translate('promo.code')
                            : translate('promo.gift')) +
                            ' ' +
                            rewardByPromo.promo_code}
                    </Button>
                ) : null}
                {selectedSnake && (
                    <PresentModal
                        isOpened
                        snake={selectedSnake}
                        collectionName={collectionName}
                        count={snakesAmount[selectedSnake.id]}
                        onClose={() => setSelectedSnake(null)}
                        onClickPrimaryBtn={onPrizeModalClick}
                    />
                )}
            </div>
        );
    }
);
